/* eslint-disable prefer-template, react/no-array-index-key, react/jsx-one-expression-per-line, jsx-a11y/media-has-caption, max-len */
import React, { useCallback, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { IoChevronBack } from 'react-icons/io5';
import { HiLink, HiExternalLink } from 'react-icons/hi';
import { FiCalendar } from 'react-icons/fi';

import {
  getDrugAction,
  getCompaniesAction,
  getTherapeuticAreasAction,
} from '../../actions/contentActions';
import { API_URL } from '../../actions';
import { generateKey, isValidDate } from '../../utils/utils';
import BoxPanel from '../../components/BoxPanel/BoxPanel';
import styles from './DrugView.module.scss';

function ExtUrl({ href }) {
  if (!href) return null;
  return (
    <div className={styles.url}>
      <a href={href} target="_blank" rel="noreferrer">
        <p>{href}</p>
        <HiExternalLink size={18} />
      </a>
    </div>
  );
}

function DateRow({ date, url }) {
  if (!date && !url) return null;
  return (
    <div className={`${styles.row} ${styles['date-row']}`}>
      {date && isValidDate(date) && (
        <div className={styles.date}>
          <FiCalendar size={20} />
          <span>{format(new Date(date), 'dd/MM/yyyy')}</span>
        </div>
      )}
      <ExtUrl href={url} />
    </div>
  );
}

export default function DrugView() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const drug = useSelector((state) => state.content.drug);
  const variations = useSelector((state) => state.content.drugVariations) || [];
  const companies = useSelector((state) => state.content.companies) || [];
  const therapeuticAreas = useSelector((state) => state.content.therapeuticAreas) || [];

  const variationUrl = (variationId) => {
    const base = process.env.REACT_APP_VARIOCTA_URL;
    if (!base) return null;
    return `${base}/variation/${variationId}`;
  };

  const companyName = companies.find((c) => c._id === drug?.company)?.name
    || drug?.company?.name
    || drug?.laboratory
    || '—';

  const load = useCallback(() => {
    if (!id) return;
    getDrugAction(dispatch, id);
    getCompaniesAction(dispatch);
    getTherapeuticAreasAction(dispatch);
  }, [dispatch, id]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className={styles.container}>
      <header>
        <div className={styles.title}>
          <h2>{drug?.name || 'Produit'}</h2>
          <Link to="/majee/produits" className={styles.back}>
            <IoChevronBack size={20} />
            retour à la liste
          </Link>
        </div>
      </header>

      {drug?._id && (
        <div className={styles.content}>
          <main>
            <div className={styles.infos}>
              <h2>INFORMATIONS</h2>
              <div className={styles.row}>
                <div className={styles['col-2']}>
                  <div className={styles.img}>
                    {drug?.logoDrug?.path && (
                      <img src={`${API_URL}/${drug.logoDrug.path}`} alt={drug.logoDrug?.alt || ''} />
                    )}
                  </div>
                  <div>
                    <label>Nom du produit</label>
                    <p>{drug?.name}</p>
                  </div>
                </div>
                {drug?.company && (
                  <div className={styles['col-2']}>
                    <div className={styles.img}>
                      {drug?.logoCompany?.path && (
                        <img
                          src={`${API_URL}/${drug.logoCompany.path}`}
                          alt={drug.logoCompany?.alt || ''}
                        />
                      )}
                    </div>
                    <div>
                      <label>Laboratoire</label>
                      <p>{companyName}</p>
                    </div>
                  </div>
                )}
              </div>
              {drug?.therapeuticAreas?.length > 0 && (
                <div className={styles.areas}>
                  <label className={styles.bold}>Aire thérapeutique</label>
                  <div className={styles['tags-list']}>
                    {drug.therapeuticAreas.map((t, i) => {
                      const name = therapeuticAreas.find((opt) => opt._id === t)?.name
                        || t?.name
                        || t;
                      return (
                        <p key={generateKey('therapeuticArea', i)} className={styles.tag}>{name}</p>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <BoxPanel label="Process de fabrication" defaultOpen={false}>
              <div className={styles.process}>
                <div className={styles.row}>
                  <div className={styles['col-2']}>
                    <label className={styles.bold}>Etapes de fabrication</label>
                    <ul>
                      {drug?.fabricationProcess?.map((f, i) => (
                        <li key={'process' + i}>{f}</li>
                      ))}
                    </ul>
                  </div>
                  <div className={styles['col-2']}>
                    <label className={styles.bold}>Etapes de sécurisation</label>
                    <ul>
                      {drug?.viralSecurity?.map((f, i) => (
                        <li key={'security' + i}>{f}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </BoxPanel>

            <BoxPanel label="COMPOSITION QUALITATIVE ET QUANTITATIVE" defaultOpen={false}>
              <div className={styles.composition}>
                <div className={styles.list}>
                  <div className={styles.labels}>
                    <p className={styles.excipient}>Concentration</p>
                    <p className={styles.excipient}>Dosage</p>
                    <p className={styles.excipient}>Volume</p>
                  </div>
                  {drug?.composition?.map((d, i) => (
                    <div key={d._id || i} className={styles.item}>
                      <p className={styles['compo-item']}>{d?.concentration}</p>
                      <p className={styles['compo-item']}>{d?.dosage}</p>
                      <p className={styles['compo-item']}>{d?.volume}</p>
                    </div>
                  ))}
                </div>
                <div className={styles.border}>
                  <h2>FORME PHARMACEUTIQUE</h2>
                  <div className={styles.row}>
                    {drug?.form && <div className={styles.form}>{drug.form}</div>}
                    <p className={styles['form-description']}>{drug?.formDescription}</p>
                  </div>
                </div>
              </div>
            </BoxPanel>

            <BoxPanel label="Excipients" defaultOpen={false}>
              <div className={styles.excipients}>
                <div className={styles.list}>
                  {drug?.excipient?.map((e) => (
                    <div key={e} className={styles.item}>
                      <p>{e}</p>
                    </div>
                  ))}
                </div>
              </div>
            </BoxPanel>

            <div className={styles.pharmaco}>
              <BoxPanel label="propriétés pharmacoLOGIQUEs" defaultOpen={false}>
                <BoxPanel label="Propriétés pharmacodynamiques" isNested defaultOpen={false}>
                  <div className={styles.row}>
                    <div className={styles['col-1']}>
                      <p className={styles.text}>{drug?.property?.dynamic?.description}</p>
                    </div>
                  </div>
                </BoxPanel>
                <BoxPanel label="Propriétés pharmacocinétiques" isNested defaultOpen={false}>
                  <div className={styles.list}>
                    {drug?.property?.kinetic?.halfLife?.map((d) => (
                      <div key={d._id} className={styles.item}>
                        <p className={styles.bold}>{d?.type}</p>
                        <p>{d.method}</p>
                        <p>{d?.value} {d?.unit}</p>
                      </div>
                    ))}
                  </div>
                  <label className={styles.bold}>Récupération</label>
                  <div className={styles.list}>
                    {drug?.property?.kinetic?.recuperation?.map((d) => (
                      <div key={d._id} className={styles.item}>
                        <p className={styles.bold}>{d?.type}</p>
                        <p>{d.method}</p>
                        <p>{d?.value} {d?.unit}</p>
                      </div>
                    ))}
                  </div>
                </BoxPanel>
              </BoxPanel>
            </div>

            <BoxPanel label="Données pharmaceutiques" defaultOpen={false}>
              <div className={styles.data}>
                <label className={styles.bold}>Incompatibilités</label>
                <div className={styles.list}>
                  {drug?.data?.incompatibilities?.map((inc, i) => (
                    <div key={'incompatibility' + i} className={styles.item}>
                      <span className={styles.bold}>{inc}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.data}>
                <label className={styles.bold}>Conservation</label>
                <div className={styles.list}>
                  {drug?.data?.conservation?.map((c, i) => (
                    <div key={'conservation' + i} className={styles.item}>
                      <p className={styles.bold}>{c.situation}</p>
                      <p>{c?.duration} {c?.durationUnit}</p>
                      <p>{c?.temperature} {c?.temperatureUnit}</p>
                    </div>
                  ))}
                </div>
              </div>
            </BoxPanel>

            <BoxPanel label="RECONSTITUTION" defaultOpen={false}>
              <div className={styles.reconstitution}>
                <label>Description</label>
                <p>{drug?.reconstitution?.description}</p>
                {drug?.reconstitution?.file?.path && (
                  <div className={styles.media}>
                    {drug.reconstitution.file.mimetype === 'video/mp4' ? (
                      <video controls>
                        <source
                          src={`${API_URL}/${drug.reconstitution.file.path}`}
                          type={drug.reconstitution.file.mimetype}
                        />
                      </video>
                    ) : (
                      <img
                        src={`${API_URL}/${drug.reconstitution.file.path}`}
                        alt={drug.reconstitution.file.alt || ''}
                      />
                    )}
                  </div>
                )}
              </div>
            </BoxPanel>

            <BoxPanel label="ÉTUDES" defaultOpen={false}>
              {[
                { key: 'pivotal', label: 'Études PIVOT' },
                { key: 'articles', label: 'Articles' },
                { key: 'studies', label: 'Études' },
                { key: 'web', label: 'Liens' },
              ].map((section) => (
                <div key={section.key} className={styles.study}>
                  <label className={styles.bold}>{section.label}</label>
                  <div className={styles.list}>
                    {drug?.studies?.[section.key]?.map((a, i) => (
                      <a
                        key={section.key + i}
                        href={a.url}
                        target="_blank"
                        rel="noreferrer"
                        className={styles.link}
                      >
                        <span className={styles.bold}>{a.name}</span>
                        <HiLink size={18} />
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </BoxPanel>

            <BoxPanel label="RÉGLEMENTATION" defaultOpen={false}>
              <div className={styles.regulations}>
                <label className={styles.bold}>DCI</label>
                <p>{drug?.regulations?.dci}</p>
              </div>
              <div className={styles.border}>
                <label className={styles.bold}>Date AMM</label>
                <DateRow date={drug?.regulations?.amm?.date} url={drug?.regulations?.amm?.url} />
                <DateRow date={drug?.regulations?.amm?.dateRenew} />
              </div>
              <div className={styles.border}>
                <label className={styles.bold}>Sunset Clause</label>
                <DateRow date={drug?.regulations?.sunset?.date} url={drug?.regulations?.sunset?.url} />
              </div>
              <div className={styles.border}>
                <label className={styles.bold}>Sunset Clause prévisionelle</label>
                <DateRow
                  date={drug?.regulations?.forecastSunset?.date}
                  url={drug?.regulations?.forecastSunset?.url}
                />
              </div>
            </BoxPanel>

            <BoxPanel label="CIP" defaultOpen={false}>
              <div className={styles.cips}>
                {drug?.regulations?.cip?.map((c, i) => (
                  <div key={generateKey('cip', i)} className={styles.cip}>
                    <div className={styles.row}>
                      <div className={styles.col}>N° {c.label}</div>
                      <div className={styles.col}>Libellé : {c.number}</div>
                      <div className={styles.col}>
                        Date 1er commerce :{' '}
                        {isValidDate(c?.firstDate) && format(new Date(c.firstDate), 'dd/MM/yyyy')}
                      </div>
                    </div>
                    <div className={styles.row}>
                      <div className={styles.col}>
                        Date arrêt :{' '}
                        {isValidDate(c?.stopDate) && format(new Date(c.stopDate), 'dd/MM/yyyy')}
                      </div>
                      <div className={styles.url}>
                        {c?.url && (
                          <a href={c.url} target="_blank" rel="noreferrer">{c.url}</a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </BoxPanel>

            <BoxPanel label="procédures" defaultOpen={false}>
              {drug?.regulations?.procedure?.registration && (
                <div className={styles.regulations}>
                  <label className={styles.bold}>Enregistrement</label>
                  <p className={styles.tag}>{drug.regulations.procedure.registration}</p>
                </div>
              )}
              <div className={styles.border}>
                <label className={styles.bold}>RCP en vigueur</label>
                <DateRow
                  date={drug?.regulations?.procedure?.rcp?.date}
                  url={drug?.regulations?.procedure?.rcp?.url}
                />
              </div>
              <div className={styles.border}>
                <label className={styles.bold}>Avis de transparence en vigueur</label>
                <DateRow
                  date={drug?.regulations?.procedure?.rcpNotice?.date}
                  url={drug?.regulations?.procedure?.rcpNotice?.url}
                />
              </div>
              {drug?.regulations?.procedure?.jo?.length > 0 && (
                <div className={styles.border}>
                  <label className={styles.bold}>JO en vigueur </label>
                  {drug.regulations.procedure.jo.map((jo) => (
                    <DateRow key={jo._id} date={jo?.date} url={jo?.url} />
                  ))}
                </div>
              )}
              {drug?.regulations?.procedure?.tr && (
                <div className={`${styles.regulations} ${styles.border}`}>
                  <label className={styles.bold}>TR</label>
                  <div className={styles.list}>
                    {drug.regulations.procedure.tr.map((tr) => (
                      <div key={tr._id} className={styles.item}>
                        <p>{tr.name}</p>
                        <p>{tr.value} €</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className={`${styles.regulations} ${styles.border}`}>
                <label className={styles.bold}>MITM</label>
                <p className={styles.tag}>{drug?.regulations?.procedure?.mitm ? 'OUI' : 'NON'}</p>
              </div>
              <div className={`${styles.pgp} ${styles.border}`}>
                <label className={styles.bold}>PGP</label>
                <p className={styles.tag}>
                  {drug?.regulations?.procedure?.pgp?.isPgp ? 'OUI' : 'NON'}
                </p>
                <DateRow
                  date={drug?.regulations?.procedure?.pgp?.date}
                  url={drug?.regulations?.procedure?.pgp?.url}
                />
              </div>
            </BoxPanel>
          </main>

          <aside>
            <BoxPanel label="Données cliniques" defaultOpen={false}>
              <BoxPanel label="Indications" isNested defaultOpen={false}>
                <div className={styles.list}>
                  {drug?.clinicalData?.indication?.map((e, i) => (
                    <div key={'indication' + i} className={styles.item}>
                      <span className={styles.bold}>{e}</span>
                    </div>
                  ))}
                </div>
              </BoxPanel>
              <BoxPanel label="Posologie" isNested defaultOpen={false}>
                {drug?.clinicalData?.dosageDescription && (
                  <p className={styles.bold}>{drug.clinicalData.dosageDescription}</p>
                )}
                <div className={styles.list}>
                  {drug?.clinicalData?.dosage?.map((d, i) => (
                    <div key={'dosage' + i} className={styles.item}>
                      <p className={styles.bold}>{d.situation}</p>
                      <p>{d.speed}</p>
                    </div>
                  ))}
                </div>
              </BoxPanel>
              {drug?.administration && (
                <BoxPanel label="Mode d’administration" isNested defaultOpen={false}>
                  <div className={styles.list}>
                    <div className={styles.item}>
                      <p className={styles.bold}>{drug.administration}</p>
                    </div>
                  </div>
                </BoxPanel>
              )}
              <BoxPanel label="Contre-indications" isNested defaultOpen={false}>
                <div className={styles.list}>
                  {drug?.clinicalData?.contraindication?.map((c, i) => (
                    <div key={'contre-indication' + i} className={styles.item}>
                      <span className={styles.bold}>{c}</span>
                    </div>
                  ))}
                </div>
              </BoxPanel>
              <BoxPanel label="Mise en garde" isNested defaultOpen={false}>
                <div className={styles.list}>
                  {drug?.clinicalData?.warning?.map((c, i) => (
                    <div key={'warning' + i} className={styles.item}>
                      <span className={styles.bold}>{c}</span>
                    </div>
                  ))}
                </div>
              </BoxPanel>
              <BoxPanel label="Interactions" isNested defaultOpen={false}>
                <div className={styles.list}>
                  {drug?.clinicalData?.interactions?.map((c, i) => (
                    <div key={'interactions' + i} className={styles.item}>
                      <span className={styles.bold}>{c}</span>
                    </div>
                  ))}
                </div>
              </BoxPanel>
              <BoxPanel label="Grossesse" isNested defaultOpen={false}>
                <div className={styles.list}>
                  {drug?.clinicalData?.pregnancy?.map((c, i) => (
                    <div key={'pregnancy' + i} className={styles.item}>
                      <span className={styles.bold}>{c}</span>
                    </div>
                  ))}
                </div>
              </BoxPanel>
              <BoxPanel label="Effets indésirables" isNested defaultOpen={false}>
                <div className={styles.list}>
                  {drug?.clinicalData?.undesirableEffect?.map((u, i) => (
                    <div key={'undesirableEffect' + i} className={styles.item}>
                      <span className={styles.bold}>{u.name}</span>
                      <span>{u.frequency}</span>
                    </div>
                  ))}
                </div>
              </BoxPanel>
              <BoxPanel label="Surdosage" isNested defaultOpen={false}>
                <p>{drug?.clinicalData?.overdose}</p>
              </BoxPanel>
            </BoxPanel>

            <BoxPanel label="CONDITIONNEMENT" defaultOpen={false}>
              <BoxPanel label="Flacon" isNested defaultOpen={false}>
                <div className={styles.list}>
                  {drug?.conditioning?.bottles?.map((u, i) => (
                    <div key={'bottles' + i} className={styles.item}>
                      <span className={styles.bold}>{u.name}</span>
                      <span>{u.dose}</span>
                    </div>
                  ))}
                </div>
              </BoxPanel>
              <BoxPanel label="Contenu boîte" isNested defaultOpen={false}>
                <div className={styles.list}>
                  {drug?.conditioning?.boxes?.map((c, i) => (
                    <div key={'boxes' + i} className={styles.item}>
                      <span className={styles.bold}>{c}</span>
                    </div>
                  ))}
                </div>
              </BoxPanel>
            </BoxPanel>

            <BoxPanel label="SITE" defaultOpen={false}>
              {drug?.regulations?.locations?.fabrication && (
                <div className={styles.site}>
                  <label className={styles.bold}>Site de fabrication</label>
                  <div className={styles['tags-list']}>
                    {drug.regulations.locations.fabrication.map((f, i) => (
                      <p key={generateKey('fabrication', i)} className={styles.tag}>{f}</p>
                    ))}
                  </div>
                </div>
              )}
            </BoxPanel>

            <BoxPanel label="VARIATIONS par conditionnement" defaultOpen={false}>
              <div className={`${styles.list} ${styles.variations}`}>
                {drug?.regulations?.variations?.map((v, i) => (
                  <div key={'variation' + i} className={styles.item}>
                    <span className={styles.bold}>{v}</span>
                  </div>
                ))}
              </div>
            </BoxPanel>

            {variations.length > 0 && (
              <BoxPanel label="VARIATIONS" defaultOpen={false}>
                <div className={`${styles.list} ${styles.variations}`}>
                  {variations.map((v) => {
                    const href = variationUrl(v._id);
                    if (href) {
                      return (
                        <a
                          key={'variation-link' + v._id}
                          href={href}
                          target="_blank"
                          rel="noreferrer"
                          className={styles.link}
                        >
                          <span className={styles.bold}>{v.number}</span>
                          <HiLink size={18} />
                        </a>
                      );
                    }
                    return (
                      <div key={'variation-link' + v._id} className={styles.item}>
                        <span className={styles.bold}>{v.number}</span>
                      </div>
                    );
                  })}
                </div>
              </BoxPanel>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}
